import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import axios from '../axios';

/** Lo que manda el backend cuando aplica una novedad de Mercado Libre. */
export interface RealtimeEvent {
    id: number;
    topic: string;
    resource: string;
    title: string;
    body: string | null;
    entityId: string | null;
    occurredAt: string;
}

type Oyente = (evento: RealtimeEvent) => void;

const oyentes = new Set<Oyente>();
const oyentesDeReconexion = new Set<() => void>();
let conexion: HubConnection | null = null;
let reintento: ReturnType<typeof setTimeout> | null = null;
let iniciando = false;

const urlDelHub = () => {
    // La misma base que usa axios: así la vista previa puede apuntar a otra instancia.
    const base = axios.defaults.baseURL || import.meta.env.VITE_BACKEND_URL || '/';
    return `${String(base).replace(/\/+$/, '')}/api/hubs/notifications`;
};

/**
 * Abre el canal una sola vez para toda la aplicación. SignalR reconecta solo; si el
 * servidor no está, se reintenta sin romper nada: el aviso en vivo es un extra.
 */
export const conectarTiempoReal = async () => {
    if (conexion) return conexion;

    conexion = new HubConnectionBuilder()
        .withUrl(urlDelHub(), {
            accessTokenFactory: () =>
                localStorage.getItem('access_token') ?? sessionStorage.getItem('access_token') ?? ''
        })
        // Espera creciente entre reintentos para no castigar a un servidor que está volviendo.
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Warning)
        .build();

    conexion.on('notification', (evento: RealtimeEvent) => {
        oyentes.forEach((oyente) => oyente(evento));
    });
    conexion.onreconnected(() => oyentesDeReconexion.forEach((oyente) => oyente()));

    await iniciarConexion(conexion);

    return conexion;
};

async function iniciarConexion(actual: HubConnection) {
    if (iniciando || actual.state !== HubConnectionState.Disconnected) return;
    iniciando = true;
    try {
        await actual.start();
        oyentesDeReconexion.forEach((oyente) => oyente());
    } catch {
        // La reconexión automática de SignalR solo funciona después del primer inicio.
        if (conexion === actual && !reintento)
            reintento = setTimeout(() => {
                reintento = null;
                void iniciarConexion(actual);
            }, 5000);
    } finally {
        iniciando = false;
    }
}

export const desconectarTiempoReal = async () => {
    if (reintento) clearTimeout(reintento);
    reintento = null;
    if (!conexion) return;
    if (conexion.state !== HubConnectionState.Disconnected) await conexion.stop();
    conexion = null;
};

/** Se suscribe a los avisos; devuelve cómo dejar de escucharlos. */
export const alRecibirNotificacion = (oyente: Oyente) => {
    oyentes.add(oyente);
    return () => oyentes.delete(oyente);
};

export const alReconectarNotificaciones = (oyente: () => void) => {
    oyentesDeReconexion.add(oyente);
    return () => oyentesDeReconexion.delete(oyente);
};

export const estadoTiempoReal = () => conexion?.state ?? HubConnectionState.Disconnected;
